// Provider definitions surface in the AI-config UI for adding /
// rotating customer-supplied API keys. Order is the suggested
// "primary first" listing.
export const PROVIDERS = [
  { id: "openrouter", label: "OpenRouter", placeholder: "sk-or-v1-...", primary: true, url: "https://openrouter.ai/keys" },
  { id: "anthropic", label: "Anthropic", placeholder: "sk-ant-...", primary: false, url: "https://console.anthropic.com/settings/keys" },
  { id: "openai", label: "OpenAI", placeholder: "sk-proj-...", primary: false, url: "https://platform.openai.com/api-keys" },
  { id: "gemini", label: "Google Gemini", placeholder: "AIza...", primary: false, url: "https://aistudio.google.com/apikey" },
] as const;

// User-facing capability groupings shown in the AI-config UI. Each
// references one or more backend Capability ids — coverage comes from
// the server-authoritative AiStatus.capabilities array (the server
// owns the catalog mapping of capability -> models -> providers).
//
// Server-side enum values (lowercased): "chat", "embedding",
// "audio_tts", "audio_stt", "image_gen".
//
// `partial` is shown when SOME of `backendCapabilities` are covered
// but not all (e.g. STT without TTS, or chat without embeddings).
export type CapabilityDef = {
  id: string;
  name: string;
  tooltip: string;
  // Backend capability ids this user-facing grouping aggregates. Fully
  // covered iff EVERY entry is covered by the server response.
  backendCapabilities: string[];
  // Provider ids the customer is most likely to need to enable this
  // grouping. Surfaced as "Needs ... key" copy when uncovered.
  hintProviders: string[];
  partial?: { message: string };
};

export const CAPABILITIES: CapabilityDef[] = [
  {
    id: "agent",
    name: "AI Assistant",
    tooltip: "The main AI that powers your OpenClaw. Handles conversations, executes tasks, uses tools, and understands images and documents.",
    backendCapabilities: ["chat"],
    hintProviders: ["anthropic", "openai", "openrouter", "gemini"],
  },
  {
    id: "memory",
    name: "Memory & Recall",
    tooltip: "Your AI stores memories from conversations automatically. Without an embedding model, memories are still saved and can be found when specifically asked for. With an embedding model, the AI automatically searches relevant memories during every conversation.",
    backendCapabilities: ["embedding"],
    hintProviders: ["openai", "openrouter"],
    partial: {
      message: "Memories are stored and can be looked up when prompted. Add an OpenAI or OpenRouter key for fast automatic recall.",
    },
  },
  {
    id: "voice",
    name: "Voice & Audio",
    tooltip: "Text-to-speech reads AI responses aloud. Speech-to-text transcribes voice messages so the AI can understand them.",
    backendCapabilities: ["audio_tts", "audio_stt"],
    hintProviders: ["openai"],
  },
  {
    id: "image",
    name: "Image Generation",
    tooltip: "Generate images from text prompts via the AI.",
    backendCapabilities: ["image_gen"],
    hintProviders: ["gemini"],
  },
];

export function getProviderLabel(id: string): string {
  return PROVIDERS.find((p) => p.id === id)?.label ?? id;
}
